import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Printer } from "lucide-react";
import { AccionesMenu } from "@/components/shell/AccionesMenu";
import { FilaDato, TarjetaPeriodo } from "@/components/shell/TarjetasPeriodo";
import { fmtFechaCorta, fmtMXN, fmtPct } from "@/lib/operativo";
import type { EstatusRev, MovimientoRev, PeriodoRev } from "@/lib/revolvente";
import { useOperativo } from "@/lib/operativo-store";

const BADGE: Record<EstatusRev, { icono: string; clase: string }> = {
  "SIN SALDO": { icono: "⚪", clase: "bg-surface text-navy" },
  "AL CORRIENTE": { icono: "🔵", clase: "bg-navy text-primary-foreground" },
  "PAGO MÍNIMO PENDIENTE": { icono: "🟡", clase: "bg-chart-3 text-primary-foreground" },
  VENCIDO: { icono: "🔴", clase: "bg-destructive text-primary-foreground" },
};

export function BadgeEstatusRev({ estatus }: { estatus: EstatusRev }) {
  const b = BADGE[estatus];
  return (
    <Badge className={`${b.clase} px-3 py-1 text-xs font-semibold`}>
      {b.icono} {estatus}
    </Badge>
  );
}

function Movimientos({ movs }: { movs: MovimientoRev[] }) {
  if (!movs.length)
    return <p className="text-xs text-muted-foreground">Sin movimientos en el período.</p>;
  return (
    <div className="space-y-2">
      {movs.map((m, i) => (
        <div
          key={`${m.fecha}-${i}`}
          className="rounded-md border border-navy/10 bg-surface px-3 py-2"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-xs font-medium text-navy">
              <span className="num">{fmtFechaCorta(m.fecha)}</span> · {m.tipo}
            </p>
            <p
              className={`num text-sm font-semibold ${m.abono > 0 ? "text-emerald" : m.tipo === "Rechazo" ? "text-destructive" : "text-navy"}`}
            >
              {m.abono > 0 ? `−${fmtMXN(m.abono)}` : m.cargo > 0 ? `+${fmtMXN(m.cargo)}` : "—"}
            </p>
          </div>
          <p className="text-[11px] text-muted-foreground">{m.concepto}</p>
          {m.pasos && m.pasos.length > 0 && (
            <div className="mt-1.5 border-t border-dashed pt-1.5">
              {m.pasos.map((s) => (
                <FilaDato
                  key={s.orden}
                  dato={{
                    l: `${s.orden}° ${s.concepto}`,
                    v: `${fmtMXN(s.aplicado)} · resta ${fmtMXN(s.restante)}`,
                    tono: s.aplicado > 0 ? "emerald" : "muted",
                  }}
                />
              ))}
            </div>
          )}
          <p className="num mt-1 text-[11px] text-muted-foreground">
            Saldo de capital: {fmtMXN(m.saldoCapital)}
          </p>
        </div>
      ))}
    </div>
  );
}

function TarjetaCorte({ p }: { p: PeriodoRev }) {
  return (
    <TarjetaPeriodo
      titulo={`Período ${p.numero}${p.cerrado ? "" : " (en curso)"}`}
      fecha={`${fmtFechaCorta(p.fechaInicio)} → corte ${fmtFechaCorta(p.fechaCorte)} · límite ${fmtFechaCorta(p.fechaLimite)}`}
      destacado={{ l: "Saldo al corte", v: fmtMXN(p.saldoAlCorte) }}
      resumen={[
        { l: "Pago mínimo", v: fmtMXN(p.pagoMinimo), tono: "chart3" },
        { l: "Pago para no generar intereses", v: fmtMXN(p.pagoNoGenerarIntereses) },
        { l: "Saldo anterior", v: fmtMXN(p.saldoAnterior) },
        { l: "Disposiciones del período", v: fmtMXN(p.disposiciones) },
        { l: "Pagos del período", v: fmtMXN(p.pagos), tono: "emerald" },
      ]}
      detalle={[
        { l: "Días del período", v: String(p.dias) },
        { l: "Saldo promedio diario", v: fmtMXN(p.saldoPromedioDiario) },
        { l: "Interés del período", v: fmtMXN(p.interes) },
        { l: "IVA del interés", v: fmtMXN(p.ivaInteres) },
        { l: "Comisiones del período", v: fmtMXN(p.comisiones) },
        { l: "Mora aplicada", v: fmtMXN(p.mora), tono: "destructive" },
        { l: "IVA de la mora", v: fmtMXN(p.ivaMora), tono: "destructive" },
        { l: "Pena convencional", v: fmtMXN(p.pena), tono: "destructive" },
        { l: "IVA de la pena", v: fmtMXN(p.ivaPena), tono: "destructive" },
        { l: "Saldo de capital al corte", v: fmtMXN(p.saldoCapitalAlCorte) },
        { l: "Impagos acumulados", v: String(p.impagosAcum) },
      ]}
      extra={
        <div className="mt-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-navy">
            Movimientos del período
          </p>
          <Movimientos movs={p.movimientos} />
        </div>
      }
    />
  );
}

export function EstadoCuentaRevolvente() {
  const { estadoRev: e, configRev: cfg, hoyRev } = useOperativo();
  const [historial, setHistorial] = useState(false);
  const actual = e.periodoActual;
  const cerrados = e.periodos;
  const previos = cerrados.slice(0, Math.max(0, cerrados.length - 1));
  const ultimo = e.ultimoCerrado;

  return (
    <Card className="print-block border-navy/15 p-5 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="h-4 w-1 rounded-full bg-navy" />
          <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
            Estado de cuenta revolvente
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <BadgeEstatusRev estatus={e.estatus} />
          <AccionesMenu
            acciones={[
              {
                label: "Imprimir",
                icono: <Printer className="h-4 w-4" />,
                onClick: () => window.print(),
              },
            ]}
          />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[
          { l: "Línea autorizada", v: fmtMXN(cfg.lineaAutorizada), t: "navy" as const },
          { l: "Línea disponible", v: fmtMXN(e.lineaDisponible), t: "emerald" as const },
          { l: "Saldo de capital utilizado", v: fmtMXN(e.saldoCapital), t: "navy" as const },
          {
            l: "Saldo al corte",
            v: fmtMXN(ultimo ? ultimo.saldoAlCorte : 0),
            t: "navy" as const,
          },
          { l: "Pago mínimo pendiente", v: fmtMXN(e.minimoPendiente), t: "chart3" as const },
          {
            l: "Interés del período vigente (c/IVA)",
            v: fmtMXN(e.interesVigenteTotal),
            t: "chart3" as const,
          },
          {
            l: "Total para ponerse al corriente",
            v: fmtMXN(e.totalAlCorriente),
            t: "navy" as const,
          },
          {
            l: "Pago para no generar intereses",
            v: fmtMXN(actual ? actual.pagoNoGenerarIntereses : 0),
            t: "navy" as const,
          },
          {
            l: "Fecha límite de pago",
            v: fmtFechaCorta(e.fechaLimiteVigente ?? undefined),
            t: "destructive" as const,
          },
          {
            l: "Mora devengada al día",
            v: fmtMXN(e.moraDevengada + e.ivaMoraDevengada),
            t: "destructive" as const,
          },
          {
            l: "Días de atraso a la fecha simulada",
            v: String(e.diasMora),
            t: "destructive" as const,
          },
          { l: "Total a liquidar hoy", v: fmtMXN(e.totalLiquidarHoy), t: "navy" as const },
          { l: "Utilización de la línea", v: fmtPct(e.utilizacionPct), t: "chart3" as const },
          {
            l: `Impagos consecutivos (umbral ${cfg.numImpagos})`,
            v: String(e.impagos),
            t: "destructive" as const,
          },
          { l: "Fecha simulada", v: fmtFechaCorta(hoyRev), t: "muted" as const },
        ].map((k) => (
          <div key={k.l} className="rounded-lg border border-navy/10 bg-surface px-3 py-2.5">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{k.l}</p>
            <p
              className={`num text-base font-semibold ${
                k.t === "emerald"
                  ? "text-emerald"
                  : k.t === "chart3"
                    ? "text-chart-3"
                    : k.t === "destructive"
                      ? "text-destructive"
                      : k.t === "muted"
                        ? "text-muted-foreground"
                        : "text-navy"
              }`}
            >
              {k.v}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        {actual && <TarjetaCorte p={actual} />}
        {ultimo && <TarjetaCorte p={ultimo} />}
        {previos.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setHistorial((v) => !v)}
              aria-expanded={historial}
              className="w-full rounded-md bg-navy-soft px-3 py-3 text-xs font-semibold uppercase tracking-wide text-navy"
            >
              {historial
                ? "Ocultar historial de períodos"
                : `Ver historial de períodos anteriores (${previos.length})`}
            </button>
            {historial && (
              <div className="space-y-3">
                {[...previos].reverse().map((p) => (
                  <TarjetaCorte key={p.numero} p={p} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
